/**
 * Error handling utilities and type guards
 */

import { ApiError, type ApiErrorType } from '../api/client';

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Type guard to check if an error has a status property
 */
export function hasStatus(error: unknown): error is { status: number } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as Record<string, unknown>)['status'] === 'number'
  );
}

/**
 * Type guard to check if an error has a type property
 */
export function hasErrorType(error: unknown): error is { type: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    typeof (error as Record<string, unknown>)['type'] === 'string'
  );
}

/**
 * Type guard to check if an error has a message property
 */
export function hasMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>)['message'] === 'string'
  );
}

/**
 * Extract error message from unknown error type
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }

  if (hasMessage(error)) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unknown error occurred';
}

/**
 * Check if error is a specific API error type
 */
export function isErrorType(error: unknown, type: ApiErrorType): boolean {
  if (isApiError(error)) {
    return error.type === type;
  }

  if (hasErrorType(error)) {
    return error.type === type;
  }

  return false;
}

/**
 * Check if error has a specific status code
 */
export function hasStatusCode(error: unknown, status: number): boolean {
  if (isApiError(error)) {
    return error.status === status;
  }

  if (hasStatus(error)) {
    return error.status === status;
  }

  return false;
}

/**
 * Get error details for logging
 */
export function getErrorDetails(error: unknown): {
  message: string;
  status?: number;
  type?: string;
} {
  if (isApiError(error)) {
    return {
      message: error.message,
      status: error.status,
      type: error.type,
    };
  }

  const details: { message: string; status?: number; type?: string } = {
    message: getErrorMessage(error),
  };

  if (hasStatus(error)) {
    details.status = error.status;
  }

  if (hasErrorType(error)) {
    details.type = error.type;
  }

  return details;
}
